import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Col,
  Container,
  Input,
  Row,
} from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import CommonTable from "components/CommonComponents/ReactTable/Table";
import AdminHeader from "components/Headers/AdminHeader";
import { getInstructorAssessmentReport } from "action/report";
import { instructorAssessmentReport } from "constants/report";
import { urlEncodedId } from "components/CommonComponents/encodeDecodeFunction";
import { DEFAULT_RECORDS_PER_PAGE } from "components/constant";

const INITIAL_FILTERS = {
  course: "",
  batch: "",
  assessmentName: "",
};

function AssessmentReport() {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const assessmentReportData = useSelector(
    (state) => state.report.instructorAssessmentReport,
  );

  const [pageNum, setPageNum] = useState(1);
  const [filterState, setFilterState] = useState({
    ...INITIAL_FILTERS,
    filterParams: [],
  });
  const [listState, setListState] = useState({
    isLoading: true,
    error: "",
    noRecords: false,
  });

  useEffect(() => {
    getInstructorAssessmentReport(dispatch, filterState.filterParams, pageNum);

    return () => {
      dispatch({
        type: instructorAssessmentReport,
        payload: {},
      });
    };
  }, [dispatch, pageNum, filterState.filterParams]);

  useEffect(() => {
    if (assessmentReportData?.code === 200) {
      const rows = assessmentReportData?.data?.trainerAssessmentReport || [];

      setListState({
        isLoading: false,
        error: "",
        noRecords: rows.length === 0,
      });
    } else if (assessmentReportData?.code || assessmentReportData?.message) {
      setListState({
        isLoading: false,
        error:
          assessmentReportData?.message || "Failed to load assessment report.",
        noRecords: false,
      });
    }
  }, [assessmentReportData]);

  const buildFilterParams = () => {
    const filters = [];

    if (filterState.course.trim()) {
      filters.push({
        field: "CourseName",
        condition: "like",
        filterValues: [filterState.course.trim()],
      });
    }

    if (filterState.batch.trim()) {
      filters.push({
        field: "BatchName",
        condition: "like",
        filterValues: [filterState.batch.trim()],
      });
    }

    if (filterState.assessmentName.trim()) {
      filters.push({
        field: "AssessmentName",
        condition: "like",
        filterValues: [filterState.assessmentName.trim()],
      });
    }

    return filters;
  };

  const handleSearch = () => {
    const newFilterParams = buildFilterParams();

    if (
      JSON.stringify(filterState.filterParams) !==
      JSON.stringify(newFilterParams)
    ) {
      setFilterState((prev) => ({
        ...prev,
        filterParams: newFilterParams,
      }));
      setPageNum(1);
    }
  };

  const handleClear = () => {
    setFilterState({
      ...INITIAL_FILTERS,
      filterParams: [],
    });
    setPageNum(1);
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleSearch();
    }
  };

  const handleFilterChange = (event) => {
    const { name, value } = event.target;

    setFilterState((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const columns = [
    {
      Header: "Sr. No.",
      accessor: "srNo",
      minWidth: 70,
      maxWidth: 80,
      centerAlign: true,
      Cell: ({ row }) =>
        (pageNum - 1) *
          (assessmentReportData?.data?.noOfRecordsPerPage ||
            DEFAULT_RECORDS_PER_PAGE) +
        row.index +
        1,
    },
    {
      Header: "Course",
      accessor: "courseName",
      minWidth: 200,
      maxWidth: 200,
      Cell: ({ value }) => <div className="text-wrapper">{value || "—"}</div>,
    },
    {
      Header: "Batch",
      accessor: "batchName",
      minWidth: 190,
      maxWidth: 190,
      Cell: ({ value }) => <div className="text-wrapper">{value || "—"}</div>,
    },
    {
      Header: "Assessment Name",
      accessor: "assessmentName",
      minWidth: 200,
      maxWidth: 200,
      Cell: ({ value }) => <div className="text-wrapper">{value || "—"}</div>,
    },
    {
      Header: "Attempted Trainee Count",
      accessor: "attemptedTrainees",
      minWidth: 200,
      maxWidth: 200,
      centerAlign: true,
      Cell: ({ value }) => <span>{value || "0 / 0"}</span>,
    },
    {
      Header: "Pass Trainee",
      accessor: "passTraineeCount",
      minWidth: 115,
      maxWidth: 115,
      centerAlign: true,
    },
    {
      Header: "Fail Trainee",
      accessor: "failTraineeCount",
      minWidth: 115,
      maxWidth: 115,
      centerAlign: true,
    },
    {
      Header: "Action",
      accessor: "action",
      minWidth: 80,
      maxWidth: 80,
      centerAlign: true,
      disableSortBy: true,
      Cell: ({ row }) => {
        const assessmentId = urlEncodedId(row.original.assessmentID);
        const batchId = urlEncodedId(row.original.batchID);

        return (
          <Button
            color="link"
            className="p-0"
            onClick={() =>
              navigate(
                `/instructor/reports/assessment-report/${assessmentId}/details/${batchId}`,
                {
                  state: {
                    assessmentId: row.original.assessmentID,
                    batchId: row.original.batchID,
                  },
                },
              )
            }
            title="View Assessment Details"
          >
            <i className="fa fa-eye table-action-primary" />
          </Button>
        );
      },
    },
  ];

  return (
    <>
      <AdminHeader />

      <Container fluid className="mt--6">
        <Row>
          <Col md="12">
            <Card>
              <CardHeader className="d-flex align-items-center justify-content-between">
                <CardTitle tag="h3" className="mb-0 text-default">
                  Assessment Report
                </CardTitle>
              </CardHeader>

              <CardBody>
                <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap flex-column flex-md-row">
                  <div className="d-flex flex-wrap align-items-center filter-group">
                    <Input
                      type="text"
                      name="course"
                      placeholder="Search by course name"
                      className="mr-2 mb-2 filter-input-large"
                      value={filterState.course}
                      onChange={handleFilterChange}
                      onKeyDown={handleKeyDown}
                    />

                    <Input
                      type="text"
                      name="batch"
                      placeholder="Search by batch name"
                      className="mr-2 mb-2 filter-input-large"
                      value={filterState.batch}
                      onChange={handleFilterChange}
                      onKeyDown={handleKeyDown}
                    />

                    <Input
                      type="text"
                      name="assessmentName"
                      placeholder="Search by assessment name"
                      className="mr-2 mb-2 filter-input-large"
                      value={filterState.assessmentName}
                      onChange={handleFilterChange}
                      onKeyDown={handleKeyDown}
                    />

                    <Button
                      color="default"
                      onClick={handleSearch}
                      className="d-flex align-items-center mb-2 filter-btn btn-deep-blue"
                    >
                      <i className="fa fa-search mr-1" />
                      Search
                    </Button>

                    {filterState.filterParams.length > 0 && (
                      <Button
                        color="default"
                        className="mb-2 filter-btn btn-simple btn-deep-blue"
                        onClick={handleClear}
                      >
                        <i className="fa fa-times mr-1" />
                        Clear
                      </Button>
                    )}
                  </div>
                </div>

                <div className="mt-2 mb-3 mx-1">
                  {assessmentReportData?.data?.totalRecords > 0 && (
                    <>
                      {filterState.filterParams.length > 0 ? (
                        <span className="total-records">
                          Showing {assessmentReportData?.data?.totalFilteredRecords} of{" "}
                          {assessmentReportData?.data?.totalRecords}{" "}
                          Records
                        </span>
                      ) : (
                        <span className="total-records">
                          Total Records :{" "}
                          {assessmentReportData?.data?.totalRecords}
                        </span>
                      )}
                    </>
                  )}
                </div>

                {listState.isLoading ? (
                  <div className="loaderContainer">
                    <i className="fa fa-spinner fa-spin fa-2xl loader" />
                  </div>
                ) :listState.error ? (
                  <div className="loaderContainer empty-style">
                    <div className="empty-icon">
                      <i className="fa-solid fa-circle-info" />
                    </div>
                    <span>{listState.error}</span>
                  </div>
                ) : listState.noRecords ? (
                  <div className="loaderContainer empty-style">
                    <div className="empty-icon">
                      <i className="fa-solid fa-circle-info" />
                    </div>
                    <span>No assessment records found.</span>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <CommonTable
                      columns={columns}
                      data={assessmentReportData?.data?.trainerAssessmentReport || []}
                      defaultPageSize={DEFAULT_RECORDS_PER_PAGE}
                      activePage={pageNum}
                      setPageNum={setPageNum}
                      totalPages={Math.ceil(
                        assessmentReportData?.data?.totalFilteredRecords /
                          assessmentReportData?.data?.noOfRecordsPerPage ||
                          DEFAULT_RECORDS_PER_PAGE,
                      )}
                      showPaginationBottom={true}
                    />
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
}

export default AssessmentReport;
